import { Button, Checkbox, Form, Modal, Space, Typography } from 'antd';
import React, { useState } from 'react';

import sharedStyles from '../../sharedStyles.module.scss';
import {
  checkIsDisablePayNowButton,
  getTotalPrice,
  handleSubmitReservationButtonClick,
  validatePaidPayment,
  validateTicketField,
} from './helper';
import styles from './index.module.scss';
import type {
  BuyItem,
  FormSubmitProps,
  PaymentInfoFormValues,
  PaymentInfoModalProps,
} from './types';

const { Item, useForm } = Form;
const { Text } = Typography;

const PaymentInfoModal = ({
  cleanKinds,
  cleanReserveList,
  cleanKindInfo,
  closeMainModal,
  validateMainFormValues,
}: PaymentInfoModalProps) => {
  const [form] = useForm<PaymentInfoFormValues>();

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isCheckTicket, setIsCheckTicket] = useState(false);
  const [isCheckPoint, setIsCheckPoint] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isShowFailedPaymentMessage, setIsShowFailedPaymentMessage] =
    useState(false);

  const handleFormValuesChange = (changedValues: PaymentInfoFormValues) => {
    if (Object.prototype.hasOwnProperty.call(changedValues, 'isCheckTicket')) {
      setIsCheckTicket(changedValues.isCheckTicket);
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, 'isCheckPoint')) {
      setIsCheckPoint(changedValues.isCheckPoint);
    }
  };

  const ticketAmount = cleanReserveList?.clean.length;

  const freeItem = cleanKindInfo?.cleanServiceItems.find(
    (item) => item.priceInfo.kind === 'Free'
  );
  const paidItems = cleanKindInfo?.cleanServiceItems.filter(
    (item) => item.priceInfo.kind !== 'Free'
  );

  const buyItems = cleanReserveList?.clean.reduce(
    (buyItems: BuyItem[], reservation) => {
      reservation.items.forEach((item) => {
        if (freeItem && item.isFreeItem) {
          const item = {
            name: freeItem.name,
            priceInfo: freeItem.priceInfo,
            count: 1,
            executionTime: reservation.date.startOf('date').valueOf(),
          };
          buyItems.push(item);
        }

        const paidItem = paidItems?.find(
          (paidItem) => paidItem._id === item.id
        );
        if (paidItem) {
          const buyItem = {
            name: paidItem.name,
            priceInfo: paidItem.priceInfo,
            count: item.quantity ?? 0,
            executionTime: reservation.date.startOf('date').valueOf(),
          };
          buyItems.push(buyItem);
        }
      });

      return buyItems;
    },
    []
  );

  const requiredPointAmount = buyItems?.reduce(
    (pointAmount: number, buyItem) => {
      if (
        buyItem.priceInfo.kind === 'Point' ||
        buyItem.priceInfo.kind === 'Both'
      ) {
        pointAmount =
          pointAmount + buyItem.priceInfo.pointPrice * buyItem.count;
      }
      return pointAmount;
    },
    0
  );

  const closePaymentInfoModal = (isFinishReservation: boolean) => {
    if (isCheckTicket) {
      setIsCheckTicket;
    }
    if (isCheckPoint) {
      setIsCheckPoint;
    }
    if (isShowFailedPaymentMessage) {
      setIsShowFailedPaymentMessage(false);
    }
    setIsOpenModal(false);
    if (isFinishReservation) {
      closeMainModal();
    }
  };

  const showFreeContent = cleanKindInfo?.cleanServiceItems.some(
    (item) => item.priceInfo.kind === 'Free'
  );

  const showPaidContent = cleanKindInfo?.cleanServiceItems.some(
    (item) => item.priceInfo.kind !== 'Free'
  );

  const userTicketAmount = cleanKinds?.find(
    (cleanKind) => cleanKind.cleanKindId === cleanKindInfo?._id
  )?.amount;
  const userPointAmount = cleanKinds?.find(
    (cleanKind) => cleanKind.cleanKindId === cleanKindInfo?._id
  )?.point;

  const handleFormSubmitProps: FormSubmitProps = {
    form,
    cleanReserveList,
    cleanKindInfo,
    freeItem,
    ticketAmount,
    buyItems,
    requiredPointAmount,
    updateIsWaiting: setIsWaiting,
    closePaymentInfoModal,
    isCheckPointPay: isCheckPoint,
    updateIsShowFailedPaymentMessage: setIsShowFailedPaymentMessage,
  };

  const isDisablePayNowButton = checkIsDisablePayNowButton({
    cleanReserveList,
    cleanKindInfo,
    isCheckTicket,
    isCheckPoint,
    userPointAmount,
    requiredPointAmount,
  });

  return (
    <>
      <Button
        onClick={async () => {
          try {
            await validateMainFormValues();
            setIsOpenModal(true);
          } catch (error) {
            return;
          }
        }}
        disabled={!cleanReserveList || cleanReserveList?.clean.length === 0}
      >
        前往付款
      </Button>
      <Modal
        open={isOpenModal}
        title="支付資訊"
        centered
        forceRender
        destroyOnClose
        footer={null}
        onCancel={() => closePaymentInfoModal(false)}
      >
        <Form
          form={form}
          preserve={false}
          layout="vertical"
          onValuesChange={handleFormValuesChange}
        >
          {isShowFailedPaymentMessage && (
            <div className={styles.failedPaymentMessage}>
              您的付款發生問題，請確認是否已點選付款方式、點數餘額是否足夠，若付款餘額不足請改為點選送出預約。
            </div>
          )}
          {showFreeContent && (
            <Item label={<Text type="secondary">消費票卷數目</Text>}>
              <div>{`${ticketAmount}張`}</div>
            </Item>
          )}
          {cleanReserveList && cleanKindInfo && showPaidContent && (
            <Item label={<Text type="secondary">消費金額</Text>}>
              <div>{`${getTotalPrice(
                cleanReserveList.clean,
                cleanKindInfo.cleanServiceItems
              )}元`}</div>
            </Item>
          )}
          {showFreeContent && (
            <Item label={<Text type="secondary">扣抵票券</Text>}>
              <Item
                name="isCheckTicket"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_rules, value) => {
                      return validateTicketField({
                        isCheckTicket: value,
                      });
                    },
                  },
                ]}
              >
                <Checkbox>一週一次清潔票券</Checkbox>
              </Item>
              <Space direction="vertical" className={styles['ml-24']}>
                <Space size="large">
                  <Text type="secondary">現有張數：</Text>
                  <Text>{`${userTicketAmount}張`}</Text>
                </Space>
                <Space size="large">
                  <Text type="secondary">扣除張數：</Text>
                  <Text>{`${ticketAmount}張`}</Text>
                </Space>
                <Space>
                  <Text type="secondary">預計剩餘張數：</Text>
                  <Text>
                    {ticketAmount &&
                      userTicketAmount &&
                      `${userTicketAmount - ticketAmount}張`}
                  </Text>
                </Space>
              </Space>
            </Item>
          )}
          {showPaidContent && (
            <Item label={<Text type="secondary">付費方式</Text>}>
              <Item
                name="isCheckPoint"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_rules, value) => {
                      return validatePaidPayment({
                        isCheckPoint: value,
                        userPointAmount,
                        requiredPointAmount,
                      });
                    },
                  },
                ]}
              >
                <Checkbox>
                  點數
                  {!showFreeContent &&
                  typeof userPointAmount === 'number' &&
                  typeof requiredPointAmount === 'number' &&
                  userPointAmount - requiredPointAmount < 0 ? (
                    <Text type="danger" className={styles['ml-5']}>
                      (餘額不足,僅能選擇送出預約)
                    </Text>
                  ) : null}
                </Checkbox>
              </Item>
              <Space direction="vertical" className={styles['ml-24']}>
                <Space size="large">
                  <Text type="secondary">現有點數：</Text>
                  <Text>{`${userPointAmount}點`}</Text>
                </Space>
                <Space size="large">
                  <Text type="secondary">扣除點數：</Text>
                  <Text>{`${requiredPointAmount}點`}</Text>
                </Space>
                <Space>
                  <Text type="secondary">預計剩餘點數：</Text>
                  <Text>{`${
                    typeof userPointAmount === 'number' &&
                    typeof requiredPointAmount === 'number' &&
                    userPointAmount - requiredPointAmount
                  }點`}</Text>
                </Space>
              </Space>
            </Item>
          )}
          <Item>
            <div className={styles.notice}>
              若選擇送出預約，會員需在訂單成立後24小時內，於app上進行付款，若逾時訂單將自動取消，進入歷史資料。
            </div>
          </Item>
        </Form>
        <Space className={sharedStyles.footerButtonsContainer}>
          <Button
            type="primary"
            onClick={() =>
              handleSubmitReservationButtonClick(
                'reserve',
                handleFormSubmitProps
              )
            }
            loading={isWaiting}
          >
            送出預約
          </Button>
          <Button
            disabled={isDisablePayNowButton}
            onClick={() =>
              handleSubmitReservationButtonClick('pay', handleFormSubmitProps)
            }
            loading={isWaiting}
          >
            立即付款
          </Button>
        </Space>
      </Modal>
    </>
  );
};

export default PaymentInfoModal;
